<script>
  import { createEventDispatcher, onMount, tick } from 'svelte';
  import Button from '@components/Button.svelte';
  import Input from '@components/Input.svelte';
  import Textarea from '@components/Textarea.svelte';
  import Socials from '@utils/data/socials-data';
  import UserAjax from '@ajax/user';
  import SocialLinkAjax from '@ajax/social_link';
  import queryString from 'query-string';

  const socials = Socials.socialsData;

  const dispatch = createEventDispatcher();

  export let createdUser;
  export let mobile = false;

  export let prefilledInstagramHandle = null;
  export let prefilledTiktokHandle = null;
  export let prefilledBio = null;

  let sf = queryString.parse(window.location.search).sf;
  prefilledInstagramHandle = prefilledInstagramHandle || sf;

  let instagramHandleInput = prefilledInstagramHandle;
  let tiktokHandleInput = prefilledTiktokHandle;
  let instagramErrorMessage = null;
  let instagramInvalid = false;
  let tiktokErrorMessage = null;
  let tiktokInvalid = false;
  let bioInput = prefilledBio;
  let submitButtonLoading = false;

  let validChecks = {
    tiktok: {
      icon: null,
      loading: null,
      username: null,
    },
    instagram: {
      icon: null,
      loading: null,
      username: null,
    },
  };

  $: bioInputPresent = bioInput && bioInput.length > 0;
  $: validInformation =
    (bioInputPresent && !instagramErrorMessage && !tiktokErrorMessage) ||
    (instagramHandleInput && instagramHandleInput.length > 0 && !instagramErrorMessage) ||
    (tiktokHandleInput && tiktokHandleInput.length > 0 && !tiktokErrorMessage);

  let stripSlashes = function (string) {
    return string.replace(/^\/+|\/+$/g, '');
  };

  let instagramHandleToUrl = function (instagramHandle) {
    let handle = stripSlashes(instagramHandle.toLowerCase()).trim();

    if (handle.includes('instagram.com')) {
      return handle;
    }

    if (handle.startsWith('@')) {
      handle = handle.slice(1);
    }

    return `https://www.instagram.com/${handle}`;
  };

  let tiktokHandleToUrl = function (tiktokHandle) {
    let handle = stripSlashes(tiktokHandle.toLowerCase()).trim();

    if (handle.includes('tiktok.com')) {
      return handle;
    }

    if (!handle.startsWith('@')) {
      handle = `@${handle}`;
    }

    return `https://www.tiktok.com/${handle}`;
  };

  const saveAvailableInformationAndFinish = function () {
    if (validChecks.tiktok.loading || validChecks.instagram.loading) {
      submitButtonLoading = true;
    } else {
      saveAvailableInformation();
      dispatch('finished');
    }
  };

  let setIconOrError = function (response, type, interval = null) {
    if (response.status == 'completed') {
      validChecks[type].loading = false;
      if (interval) {
        clearInterval(interval);
      }
      if (response.valid) {
        validChecks[type].icon = response.image;
        if (submitButtonLoading) {
          saveAvailableInformationAndFinish();
        } else {
          saveAvailableInformation();
        }
      } else {
        validChecks[type].icon = null;
        if (type == 'instagram') {
          instagramErrorMessage = 'No account found';
        } else if (type == 'tiktok') {
          tiktokErrorMessage = 'No account found';
        }
      }

      submitButtonLoading = false;
    }
  };

  let checkValid = async function (input, type) {
    if (!input) {
      validChecks[type] = { icon: null, loading: false, username: null };
      return;
    }

    input = input.toLowerCase();

    if (input == validChecks[type].username) {
      return;
    }

    if (type == 'instagram' && instagramInvalid) {
      return;
    }

    if (type == 'tiktok' && tiktokInvalid) {
      return;
    }

    validChecks[type] = { icon: null, loading: true, username: input };

    let response = await SocialLinkAjax.queueCheckValid(input, type);
    if (response.status == 'completed') {
      setIconOrError(response, type);
    }
    let interval;
    let maxInterval = 15000;
    interval = setInterval(async () => {
      response = await SocialLinkAjax.checkValid(input, type);
      setIconOrError(response, type, interval);
    }, 750); // Interval time in milliseconds

    setTimeout(() => {
      clearInterval(interval);
      validChecks[type].loading = false;
      if (submitButtonLoading) {
        saveAvailableInformationAndFinish();
      }
    }, maxInterval);
  };

  let saveAvailableInformation = function () {
    let socialLinks = [];
    if (instagramHandleInput && !instagramErrorMessage) {
      socialLinks.push({
        url: instagramHandleToUrl(instagramHandleInput),
        label: socials['instagram'].label,
        link_type: 'instagram',
        user_avatar_icon: validChecks['instagram'].icon,
      });
    }

    if (tiktokHandleInput && !tiktokErrorMessage) {
      socialLinks.push({
        url: tiktokHandleToUrl(tiktokHandleInput),
        label: socials['tiktok'].label,
        link_type: 'tiktok',
        user_avatar_icon: validChecks['tiktok'].icon,
      });
    }

    if (createdUser && (socialLinks.length > 0 || bioInputPresent)) {
      UserAjax.update(createdUser, { bio: bioInput, social_links: socialLinks });
    }
  };

  let validateInstagramInput = function (event) {
    let instagramRegex = /^[a-zA-Z0-9._]{1,30}$/;
    let instagramValue = event.detail.value.trim();
    if (instagramValue && !instagramRegex.test(instagramValue)) {
      instagramErrorMessage =
        'Instagram handles can only contain letters, numbers, periods, and underscores, and can’t be more than 30 characters';
      instagramInvalid = true;
    } else {
      instagramErrorMessage = null;
      instagramInvalid = false;
    }
  };

  let validateTiktokInput = function (event) {
    let tiktokRegex = /^[a-zA-Z0-9._]{1,30}$/;
    let tiktokValue = event.detail.value.trim();

    if (tiktokValue && !tiktokRegex.test(tiktokValue)) {
      tiktokErrorMessage =
        'TikTok handles can only contain letters, numbers, periods, and underscores, and can’t be more than 24 characters';
      tiktokInvalid = true;
    } else {
      tiktokErrorMessage = null;
      tiktokInvalid = false;
    }
  };

  onMount(async () => {
    await tick();

    if (prefilledInstagramHandle && prefilledInstagramHandle.length > 0) {
      checkValid(prefilledInstagramHandle, 'instagram');
    }

    if (prefilledTiktokHandle && prefilledTiktokHandle.length > 0) {
      checkValid(prefilledTiktokHandle, 'tiktok');
    }
  });
</script>

<style style="text/scss" lang="scss">
  @import '../../../vendor/design-system.scss';

  .user-bio-step {
    margin-top: 35px;

    .ui-header {
      margin-top: 24px;
      font-weight: bold;
      font-size: 22px;
      line-height: 22px;
      text-align: center;
      margin-left: auto;
      margin-right: auto;
      max-width: 530px;
      margin-bottom: 40px;
    }

    .ui-body {
      margin-left: auto;
      margin-right: auto;
      max-width: 380px;
      text-align: left;
    }

    .smiling-face {
      img {
        width: 64px;
        height: 64px;
      }
    }
  }

  .mobile {
    .smiling-face {
      margin-top: 60px;
    }

    .ui-header {
      font-size: 25px;
      line-height: 32px;
      width: 260px;
      max-width: 260px;
    }

    .ui-body {
      width: 295px;
      max-width: 295px;
    }

    .skip-btn {
      right: 30px;
      top: 30px;
    }
  }

  .skip-btn {
    position: absolute;
    right: 40px;
    top: 40px;
    font-weight: 500;
    font-size: 14px;
    line-height: 17px;
    color: #7f7f7f;
    cursor: pointer;

    &:hover {
      color: #3d3d3d;
    }
  }
  .user-bio-step {
    * :global(.instagram-input-error-msg) {
      margin-top: 0px;
      margin-bottom: 15px;
    }
    * :global(.input-container input:focus) {
      background-color: $white !important;
    }

    * :global(.input-container textarea:focus) {
      background-color: $white !important;
    }
  }
</style>

<div class="user-bio-step" class:mobile={mobile}>
  {#if !bioInputPresent}
    <span
      class="skip-btn"
      on:click={() => {
        dispatch('finished');
      }}>Skip</span>
  {/if}
  <div class="smiling-face">
    <img
      alt="smiling-face"
      srcset="/images/app-invites/smiling-face.png 1x, /images/app-invites/smiling-face@2x.png 2x" />
  </div>

  <div class="ui-header">Add your socials & bio</div>
  <div class="ui-body">
    <div>
      <Input
        bind:value={instagramHandleInput}
        leftIconPath={'/images/profile/socials/instagram.svg'}
        on:change={validateInstagramInput}
        error={instagramErrorMessage}
        errorClass={'instagram-input-error-msg'}
        on:blur={() => {
          checkValid(instagramHandleInput, 'instagram');
        }}
        placeholder={'johnsmith2022'}
        loading={false}
        withRightIconSlot={true}>
        <svelte:fragment slot="right-side-icon">
          {#if validChecks.instagram.loading}
            <div class="input-icon-right">
              <i class="fa fa-spin fa-spinner" />
            </div>
          {:else if validChecks.instagram.icon}
            <div>
              <img
                alt="instagram-icon"
                height={32}
                width={32}
                style=" display: inline-block; object-fit: cover; border-radius: 9999px;"
                src={validChecks.instagram.icon} />
              <img
                alt="checkbox-icon"
                style="position: absolute; right: -5px; bottom: -5px"
                width={16}
                src="/images/rec_request/checkbox.svg" />
            </div>
          {/if}
        </svelte:fragment>
      </Input>
    </div>
    <Input
      bind:value={tiktokHandleInput}
      leftIconPath={'/images/profile/socials/tiktok.svg'}
      on:change={validateTiktokInput}
      error={tiktokErrorMessage}
      on:blur={() => {
        checkValid(tiktokHandleInput, 'tiktok');
      }}
      placeholder={'johnsmith2022'}
      loading={false}
      withRightIconSlot={true}>
      <svelte:fragment slot="right-side-icon">
        {#if validChecks.tiktok.loading}
          <div class="input-icon-right">
            <i class="fa fa-spin fa-spinner" />
          </div>
        {:else if validChecks.tiktok.icon}
          <div>
            <img
              alt="tiktok-icon"
              height={32}
              width={32}
              style=" display: inline-block; object-fit: cover; border-radius: 9999px;"
              src={validChecks.tiktok.icon} />
            <img
              alt="checkbox-icon"
              style="position: absolute; right: -5px; bottom: -5px"
              width={16}
              src="/images/rec_request/checkbox.svg" />
          </div>
        {/if}
      </svelte:fragment>
    </Input>

    <div style="margin-top:50px">
      <Textarea
        bind:value={bioInput}
        placeholder="E.g. Mom to 2 kids and a dog. Share makeup recommendations and decor inspiration. Based in NYC."
        rows={3} />
    </div>
  </div>

  <div
    style="position: absolute; bottom: 64px; {mobile
      ? 'width: 180px; left: calc(50% - 90px);'
      : 'width: 316px; left: calc(50% - 158px);'}">
    <Button
      loading={submitButtonLoading}
      on:click={saveAvailableInformationAndFinish}
      disabled={!validInformation}
      text="Next" />
  </div>
</div>
